import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { PasswordGate } from './components/PasswordGate';
import { ACADEMY_PASSWORD, ACADEMY_GATE_KEY } from './data/academyModules';
import { GraduationCap } from 'lucide-react';

import SiteLayout from './components/SiteLayout';
import Home from './pages/Home';
import Peme from './pages/Peme';
import CrewPortal from './pages/CrewPortal';
const Telemedicine = lazy(() => import('./pages/Telemedicine'));
const PemeReview = lazy(() => import('./pages/PemeReview'));
const PemeExaminations = lazy(() => import('./pages/PemeExaminations'));
const PhysicianCds = lazy(() => import('./pages/PhysicianCds'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Company = lazy(() => import('./pages/Company'));
const Evidence = lazy(() => import('./pages/Evidence'));
const TrialStarted = lazy(() => import('./pages/TrialStarted'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const Wellness = lazy(() => import('./pages/Wellness'));
const AcademyTrackSelector = lazy(() => import('./pages/AcademyTrackSelector'));
const AcademyHome = lazy(() => import('./pages/AcademyHome'));
const AcademyModule = lazy(() => import('./pages/AcademyModule'));
const AcademyCertificate = lazy(() => import('./pages/AcademyCertificate'));
const AcademyInstall = lazy(() => import('./pages/AcademyInstall'));

const wrap = (page) => (
  <SiteLayout>
    {page}
  </SiteLayout>
);

/** Old /academy/module/:id bookmarks → telemedicine track. */
function LegacyAcademyModuleRedirect() {
  const { moduleId } = useParams();
  return <Navigate to={`/academy/telemedicine/module/${moduleId}`} replace />;
}

/** Remount track pages when trackId changes so progress state re-initializes. */
function AcademyHomeKeyed() {
  const { trackId } = useParams();
  return <AcademyHome key={trackId} />;
}

function AcademyModuleKeyed() {
  const { trackId } = useParams();
  return <AcademyModule key={trackId} />;
}

function AcademyCertificateKeyed() {
  const { trackId } = useParams();
  return <AcademyCertificate key={trackId} />;
}

class RouteErrorBoundary extends React.Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (this.state.failed) {
      return (
        <main className="container-wide py-24" role="alert">
          <h1 className="font-display text-3xl text-ink">This page could not load</h1>
          <p className="mt-3 text-ink-2">Reload the page or return to the SeaScope home page.</p>
          <a className="btn-primary mt-6 inline-flex" href="/">Return to home</a>
        </main>
      );
    }
    return this.props.children;
  }
}

// Academy is password-gated. Auth persists for the browser session so
// navigating module to module does not re-prompt.
const wrapGated = (page) => (
  <PasswordGate
    correctPassword={ACADEMY_PASSWORD}
    persistToSession
    gateKey={ACADEMY_GATE_KEY}
    heading="SeaScope Academy"
    bodyText="Enter the training password provided during onboarding to access the academy."
    buttonLabel="Enter Academy"
    Icon={GraduationCap}
  >
    <SiteLayout>
      {page}
    </SiteLayout>
  </PasswordGate>
);

// Preview variants are noindex while canonicals intentionally remain on their public routes.
const isMarketingPreview = import.meta.env.VITE_MARKETING_PREVIEW === '1';

export default function App() {
  return (
    <HelmetProvider>
      {isMarketingPreview && (
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
      )}
      <RouteErrorBoundary>
        <Suspense fallback={<main className="container-wide py-24" aria-live="polite">Loading page…</main>}>
          <Routes>
        <Route path="/" element={wrap(<Home />)} />
        <Route path="/telemedicine" element={wrap(<Telemedicine />)} />
        <Route path="/peme" element={wrap(<Peme />)} />
        <Route path="/peme/review" element={wrap(<PemeReview />)} />
        <Route path="/peme/examinations" element={wrap(<PemeExaminations />)} />
        <Route path="/pricing" element={wrap(<Pricing />)} />
        <Route path="/evidence" element={wrap(<Evidence />)} />
        <Route path="/about" element={wrap(<Company />)} />
        <Route path="/press" element={<Navigate to="/about#press" replace />} />
        <Route path="/privacy" element={wrap(<Privacy />)} />
        <Route path="/terms" element={wrap(<Terms />)} />
        <Route path="/blog" element={wrap(<Blog />)} />
        <Route path="/blog/:slug" element={wrap(<BlogPost />)} />
        <Route path="/trial-started" element={wrap(<TrialStarted />)} />
        <Route path="/wellness" element={wrap(<Wellness />)} />
        <Route path="/crew-portal" element={wrap(<CrewPortal />)} />
        <Route path="/seascope-cds" element={wrap(<PhysicianCds />)} />

        {/* SeaScope Academy — password gated; track selector then Telemedicine / PEME */}
        <Route path="/academy" element={wrapGated(<AcademyTrackSelector />)} />
        {/* Static academy paths must precede /:trackId so they are not swallowed */}
        <Route path="/academy/install" element={wrapGated(<AcademyInstall />)} />
        <Route path="/academy/module/:moduleId" element={<LegacyAcademyModuleRedirect />} />
        <Route path="/academy/certificate" element={<Navigate to="/academy/telemedicine/certificate" replace />} />
        <Route
          path="/academy/:trackId"
          element={wrapGated(<AcademyHomeKeyed />)}
        />
        <Route
          path="/academy/:trackId/module/:moduleId"
          element={wrapGated(<AcademyModuleKeyed />)}
        />
        <Route
          path="/academy/:trackId/certificate"
          element={wrapGated(<AcademyCertificateKeyed />)}
        />

        {/* Redirects: never 404 a URL Google has indexed */}
        <Route path="/cargo-solutions" element={<Navigate to="/telemedicine" replace />} />
        <Route path="/security" element={<Navigate to="/evidence#security" replace />} />
        <Route path="/services" element={<Navigate to="/" replace />} />
        <Route path="/servicios" element={<Navigate to="/" replace />} />
        <Route path="/demo" element={<Navigate to="/telemedicine" replace />} />
        <Route path="/seascope" element={<Navigate to="/telemedicine" replace />} />
        <Route path="/seascope/safety" element={<Navigate to="/evidence#safety" replace />} />
        <Route path="/seascope/safety-case" element={<Navigate to="/evidence#safety-case" replace />} />
        <Route path="/seascope/regulatory" element={<Navigate to="/evidence#regulatory" replace />} />
        <Route path="/seascope/evidence" element={<Navigate to="/evidence#validation" replace />} />
        <Route path="/seascope/eval-tracker" element={<Navigate to="/evidence#eval-tracker" replace />} />
        <Route path="/seascope/data-flow" element={<Navigate to="/evidence#data-flow" replace />} />
        {/* Common dead-end spellings / legacy paths (mirror vercel.json where SPA-only) */}
        <Route path="/peme-review" element={<Navigate to="/peme/review" replace />} />
        <Route path="/peme-examinations" element={<Navigate to="/peme/examinations" replace />} />
        <Route path="/company" element={<Navigate to="/about" replace />} />
        <Route path="/crew" element={<Navigate to="/crew-portal" replace />} />
        <Route path="/install" element={<Navigate to="/academy/install" replace />} />
        <Route path="/legal" element={<Navigate to="/privacy" replace />} />
        <Route path="/physician-cds" element={<Navigate to="/seascope-cds" replace />} />
        <Route path="/cds" element={<Navigate to="/seascope-cds" replace />} />

        <Route path="*" element={wrap(<NotFound />)} />
          </Routes>
        </Suspense>
      </RouteErrorBoundary>
    </HelmetProvider>
  );
}
